import React, { useState, useEffect, useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../api';

const AdminAvatars = () => {
    const { user, isAuthenticated } = useAuth();
    const [avatars, setAvatars] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [listError, setListError] = useState(null);
    const [uploadFile, setUploadFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [uploadSuccess, setUploadSuccess] = useState('');

    const fetchAvatars = useCallback(async () => {
        setIsLoading(true);
        setListError(null);
        try {
            const res = await fetch(`${API_URL}/avatars`);
            if (!res.ok) throw new Error(`Request failed (${res.status})`);
            const data = await res.json();
            setAvatars(data.avatars || []);
        } catch {
            setListError('Unable to load avatars at this time.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isAuthenticated || !user?.isAdmin) return;
        fetchAvatars();
    }, [fetchAvatars, isAuthenticated, user]);

    if (!isAuthenticated) {
        return <Navigate to="/auth" replace />;
    }

    if (!user?.isAdmin) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="bg-slate-900/80 backdrop-blur-sm rounded-xl p-10 text-center max-w-sm">
                    <p className="text-white text-lg mb-4">Administrator access required.</p>
                    <Link to="/" className="text-amber-400 hover:underline text-sm">Return home</Link>
                </div>
            </div>
        );
    }

    const handleUpload = async (e) => {
        e.preventDefault();
        setUploadError('');
        setUploadSuccess('');

        if (!uploadFile) {
            setUploadError('Please select an image file.');
            return;
        }

        setIsUploading(true);
        try {
            const { tokens } = await fetchAuthSession();
            const token = tokens.idToken.toString();

            // Get presigned upload URL
            const metaRes = await fetch(`${API_URL}/admin/upload-url`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    filename: uploadFile.name,
                    contentType: uploadFile.type,
                    folder: 'avatars/',
                }),
            });

            if (!metaRes.ok) {
                const errData = await metaRes.json().catch(() => ({}));
                throw new Error(errData.message || `Failed to get upload URL (${metaRes.status})`);
            }

            const { uploadUrl } = await metaRes.json();

            // Upload to S3 presigned URL (no auth header)
            const uploadRes = await fetch(uploadUrl, {
                method: 'PUT',
                headers: { 'Content-Type': uploadFile.type },
                body: uploadFile,
            });

            if (!uploadRes.ok) {
                throw new Error('File upload to storage failed. Please try again.');
            }

            setUploadSuccess('Avatar uploaded successfully!');
            setUploadFile(null);
            // Refresh avatar list
            await fetchAvatars();
        } catch (err) {
            setUploadError(err.message || 'Upload failed. Please try again.');
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-900 py-12 px-6">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-white mb-1">Avatar Library</h1>
                    <p className="text-gray-400 text-sm">Upload and manage user avatar images.</p>
                </div>

                {/* Upload form */}
                <div className="bg-slate-800/70 backdrop-blur-sm border border-slate-700 rounded-xl p-6 mb-8">
                    <h2 className="text-lg font-semibold text-white mb-4">Upload New Avatar</h2>

                    {uploadSuccess && (
                        <div className="mb-4 p-3 bg-green-900/40 border border-green-600 rounded-lg">
                            <p className="text-green-300 text-sm">{uploadSuccess}</p>
                        </div>
                    )}

                    {uploadError && (
                        <div className="mb-4 p-3 bg-red-900/40 border border-red-600 rounded-lg">
                            <p className="text-red-300 text-sm">{uploadError}</p>
                        </div>
                    )}

                    <form onSubmit={handleUpload} className="flex flex-col sm:flex-row gap-4 items-start">
                        <div className="flex-1">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => setUploadFile(e.target.files[0] || null)}
                                className="w-full text-sm text-gray-300 file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:bg-slate-600 file:text-white hover:file:bg-slate-500 file:cursor-pointer"
                            />
                            {uploadFile && (
                                <p className="text-gray-500 text-xs mt-1">
                                    {uploadFile.name} ({(uploadFile.size / 1024).toFixed(1)} KB)
                                </p>
                            )}
                        </div>
                        <button
                            type="submit"
                            disabled={isUploading || !uploadFile}
                            className="bg-amber-600 hover:bg-amber-500 disabled:bg-slate-600 disabled:cursor-not-allowed text-white text-sm font-semibold px-5 py-2 rounded-full transition-colors whitespace-nowrap"
                        >
                            {isUploading ? 'Uploading...' : 'Upload Avatar'}
                        </button>
                    </form>
                </div>

                {/* Avatar grid */}
                <div className="bg-slate-800/70 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
                    <h2 className="text-lg font-semibold text-white mb-4">
                        Available Avatars
                        {!isLoading && ` (${avatars.length})`}
                    </h2>

                    {isLoading && (
                        <div className="flex justify-center py-12">
                            <div className="w-10 h-10 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" aria-label="Loading" />
                        </div>
                    )}

                    {!isLoading && listError && (
                        <p className="text-gray-400 text-center py-8">{listError}</p>
                    )}

                    {!isLoading && !listError && avatars.length === 0 && (
                        <p className="text-gray-500 text-center italic py-8">
                            No avatars uploaded yet. Upload the first one above.
                        </p>
                    )}

                    {!isLoading && !listError && avatars.length > 0 && (
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
                            {avatars.map((avatar) => (
                                <div key={avatar.id || avatar.url} className="flex flex-col items-center gap-1.5">
                                    <img
                                        src={avatar.url}
                                        alt={avatar.name || 'Avatar'}
                                        className="w-14 h-14 rounded-full object-cover border-2 border-slate-600"
                                    />
                                    <span className="text-gray-500 text-xs text-center break-all leading-tight">
                                        {avatar.name || 'avatar'}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminAvatars;
